import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  transcriptorApi,
  TranscriptorEncounterResponse,
} from '@/shared/api/transcriptor/transcriptorApi';

import { RootState } from '@/app/model/store';

import { actions } from './encounterSlice';

type FetchEncounterListArgs = {
  organizationId: string;
  page: number;
  limit: number;
  schemaCode?: string | null;
};

type FetchEncounterListResult = {
  ids: string[];
  total: number;
  page: number;
};

export const fetchEncounterList = createAsyncThunk<
  FetchEncounterListResult,
  FetchEncounterListArgs
>('encounter/fetchList', async (args, { dispatch }) => {
  const response = await transcriptorApi.getConsultations({
    limit: args.limit,
    organization_id: args.organizationId,
    page: args.page,
    resolve_patient_info: true,
    schema_code: args.schemaCode ?? undefined,
  });

  dispatch(actions.mergePreviewMany(response.encounters));

  return {
    ids: response.encounters.map((encounter) => encounter.id),
    total: response.total,
    page: args.page,
  };
});

export const fetchEncounter = createAsyncThunk<
  TranscriptorEncounterResponse,
  string
>('encounter/fetchOne', async (encounterId, { dispatch }) => {
  const encounter = await transcriptorApi.getConsultation(encounterId, {
    resolvePatientInfo: true,
  });

  dispatch(actions.upsertDetail(encounter));

  return encounter;
});

type AttachPatientThunkArgs = {
  encounterId: string;
  patientId: string;
};

export const attachPatient = createAsyncThunk<
  TranscriptorEncounterResponse,
  AttachPatientThunkArgs
>('encounter/attachPatient', async (args, { dispatch }) => {
  await transcriptorApi.updateConsultation(args.encounterId, {
    patient_id: args.patientId,
  });
  const encounter = await transcriptorApi.getConsultation(args.encounterId, {
    resolvePatientInfo: true,
  });

  dispatch(actions.upsertDetail(encounter));

  return encounter;
});

type ApproveDocumentThunkArgs = {
  encounterId: string;
  documentId: string;
  approved: boolean;
};

export const approveDocument = createAsyncThunk<
  void,
  ApproveDocumentThunkArgs,
  { state: RootState }
>('encounter/approveDocument', async (args, { dispatch, getState }) => {
  const previous = getState().encounter.byId[args.encounterId];

  if (!previous) {
    return;
  }

  dispatch(
    actions.patchDocumentApproved({
      encounterId: args.encounterId,
      documentId: args.documentId,
      approved: args.approved,
    }),
  );

  try {
    await transcriptorApi.updateConsultationDocument(
      args.encounterId,
      args.documentId,
      { approved: args.approved },
    );
  } catch (error) {
    dispatch(actions.upsertDetail(previous));
    throw error;
  }
});
